import { computePromoSplit } from './promotion';

export interface OpenOwnOrder {
  id: string;
  pos_id: string | null;
  visit_id?: string | null;
  product_id: string;
  variant_id?: string | null;
  quantity: number;
  product_name: string;
  manufacturer_id: string;
  manufacturer_name: string;
  pos_name?: string | null;
  pos_city?: string | null;
  variant_name?: string | null;
  status: string;
  promotion_type?: string;
  unit_price_override?: number | null;
  notes?: string | null;
  picked?: boolean;
  distributor_id?: string | null;
}

export interface PosOrderGroup {
  key: string;
  posId: string | null;
  posName: string;
  posCity: string;
  orders: OpenOwnOrder[];
  lineCount: number;
}

export interface ProductTotal {
  key: string;
  productName: string;
  variantName: string;
  manufacturerName: string;
  /** Physical cases to pack (paid + free). */
  quantity: number;
  paid: number;
  free: number;
}

export function formatPackQty(delivered: number, paid: number, free: number): string {
  if (free > 0) return `${delivered} (${paid}+${free})`;
  return String(delivered);
}

export function orderLineQty(
  order: Pick<OpenOwnOrder, 'quantity' | 'promotion_type'>
): string {
  const split = computePromoSplit(order.quantity, order.promotion_type);
  return `${formatPackQty(split.delivered, split.paid, split.free)}×`;
}

export function mergeOpenOrders(
  a: OpenOwnOrder[],
  b: OpenOwnOrder[]
): OpenOwnOrder[] {
  const byId = new Map<string, OpenOwnOrder>();
  for (const row of [...a, ...b]) byId.set(row.id, row);
  return [...byId.values()];
}

export function groupOrdersByPos(orders: OpenOwnOrder[]): PosOrderGroup[] {
  const groups = new Map<string, OpenOwnOrder[]>();
  for (const order of orders) {
    const key = order.pos_id || 'none';
    const list = groups.get(key) || [];
    list.push(order);
    groups.set(key, list);
  }
  return [...groups.entries()]
    .map(([key, rows]) => ({
      key,
      posId: rows[0]?.pos_id || null,
      posName: rows[0]?.pos_name || 'No PoS',
      posCity: rows[0]?.pos_city || '',
      orders: rows,
      lineCount: rows.length,
    }))
    .sort((a, b) => a.posName.localeCompare(b.posName));
}

export function pickableOrders(orders: OpenOwnOrder[]): OpenOwnOrder[] {
  return orders.filter((order) => !order.picked);
}

export function selectedOrders(
  orders: OpenOwnOrder[],
  selectedIds: Set<string>
): OpenOwnOrder[] {
  return orders.filter((order) => selectedIds.has(order.id));
}

export function uniquePosCount(orders: OpenOwnOrder[]): number {
  return new Set(orders.map((order) => order.pos_id || 'none')).size;
}

export function countDocumentGrain(orders: OpenOwnOrder[]): number {
  return new Set(
    orders.map((order) => `${order.pos_id || ''}|${order.manufacturer_id}`)
  ).size;
}

export function productTotals(orders: OpenOwnOrder[]): ProductTotal[] {
  const totals = new Map<string, ProductTotal>();
  for (const order of orders) {
    const key = `${order.product_id}|${order.variant_name || ''}`;
    const split = computePromoSplit(order.quantity, order.promotion_type);
    const existing = totals.get(key);
    if (existing) {
      existing.quantity += split.delivered;
      existing.paid += split.paid;
      existing.free += split.free;
      continue;
    }
    totals.set(key, {
      key,
      productName: order.product_name,
      variantName: order.variant_name || '',
      manufacturerName: order.manufacturer_name,
      quantity: split.delivered,
      paid: split.paid,
      free: split.free,
    });
  }
  return [...totals.values()].sort((a, b) =>
    a.productName.localeCompare(b.productName)
  );
}
